// Checks for the picture logos the AI logo maker returns as data URLs. Used by
// the ai-generate-logo function (on what Gemini sent) and again in the browser
// (on what the function sent) before anything is drawn or uploaded.
//
// Only base64 PNG / JPEG / WebP is accepted, the bytes must start with that
// format's signature, and the size is capped. No Deno or DOM APIs beyond
// atob, so this file loads in Deno, Node and the browser.

export const LOGO_IMAGE_MIME_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;
export type LogoImageMime = (typeof LOGO_IMAGE_MIME_TYPES)[number];

/** Decoded size cap per image. A 1K logo on white is usually well under 1 MB. */
export const MAX_LOGO_IMAGE_BYTES = 4 * 1024 * 1024;

const BASE64 = /^[A-Za-z0-9+/]+={0,2}$/;
const DATA_URL = /^data:(image\/(?:png|jpeg|webp));base64,([A-Za-z0-9+/]+={0,2})$/;

export type LogoImageCheck = { ok: true; mime: LogoImageMime; base64: string; bytes: number } | { ok: false; reason: string };

function decodedLength(base64: string): number {
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
}

function signatureMatches(mime: LogoImageMime, base64: string): boolean {
  let head: string;
  try {
    // 16 base64 characters = the first 12 bytes.
    head = atob(base64.slice(0, 16));
  } catch {
    return false;
  }
  const bytes = Array.from(head, (c) => c.charCodeAt(0));
  const starts = (sig: number[], at = 0) => sig.every((b, i) => bytes[at + i] === b);
  if (mime === "image/png") return starts([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);
  if (mime === "image/jpeg") return starts([0xff, 0xd8, 0xff]);
  return starts([0x52, 0x49, 0x46, 0x46]) && starts([0x57, 0x45, 0x42, 0x50], 8); // RIFF....WEBP
}

/** Check a base64 image and its declared type. */
export function checkLogoImage(mimeType: unknown, data: unknown): LogoImageCheck {
  const mime = typeof mimeType === "string" ? mimeType.trim().toLowerCase() : "";
  if (!(LOGO_IMAGE_MIME_TYPES as readonly string[]).includes(mime)) return { ok: false, reason: `type ${mime || "missing"}` };
  if (typeof data !== "string") return { ok: false, reason: "no data" };
  const base64 = data.replace(/\s+/g, "");
  if (base64.length < 16 || !BASE64.test(base64)) return { ok: false, reason: "not base64" };
  const bytes = decodedLength(base64);
  if (bytes > MAX_LOGO_IMAGE_BYTES) return { ok: false, reason: `too big (${bytes} bytes)` };
  if (!signatureMatches(mime as LogoImageMime, base64)) return { ok: false, reason: "bytes don't match the type" };
  return { ok: true, mime: mime as LogoImageMime, base64, bytes };
}

export function logoImageDataUrl(mime: LogoImageMime, base64: string): string {
  return `data:${mime};base64,${base64}`;
}

/** Validate a data URL from the function: the same checks, nothing but data:image/(png|jpeg|webp);base64. */
export function checkLogoDataUrl(url: unknown): LogoImageCheck {
  if (typeof url !== "string" || url.length > MAX_LOGO_IMAGE_BYTES * 2) return { ok: false, reason: "not a data URL" };
  const match = url.match(DATA_URL);
  if (!match) return { ok: false, reason: "not a data URL" };
  return checkLogoImage(match[1], match[2]);
}
